import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ArrowUp, ArrowDown, Minus } from 'lucide-react';

const COLORS = {
  overall: '#d4af37',
  technical: '#3b82f6',
  physical: '#10b981',
  tactical: '#a855f7'
};

const DevelopmentTimeline = ({ history, loading }) => {
  const canvasRef = useRef(null);
  const [activeMetric, setActiveMetric] = useState('overall');
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (history?.length > 1) drawChart();
  }, [history, activeMetric]);

  if (loading) {
    return (
      <div className="bg-card border border-border/40 rounded-sm p-5">
        <div className="h-40 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!history || history.length < 2) {
    return (
      <div className="bg-card border border-border/40 rounded-sm p-5">
        <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
          <TrendingUp size={14} className="text-primary" /> Development Timeline
        </h2>
        <p className="text-sm text-muted-foreground py-6 text-center">
          Not enough data for timeline. At least 2 evaluation periods needed.
        </p>
      </div>
    );
  }

  const metrics = [
    { key: 'overall', label: 'Overall', color: COLORS.overall },
    { key: 'technical', label: 'Technical', color: COLORS.technical },
    { key: 'physical', label: 'Physical', color: COLORS.physical },
    { key: 'tactical', label: 'Tactical', color: COLORS.tactical }
  ];

  const getData = (key) => history.map(h => key === 'overall' ? h.overall_score : h[`${key}_score`]);

  // Calculate trend
  const currentData = getData(activeMetric);
  const lastVal = currentData[currentData.length - 1];
  const prevVal = currentData[currentData.length - 2];
  const trend = lastVal - prevVal;

  const drawChart = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = 2;
    const rect = canvas.parentElement.getBoundingClientRect();
    const w = rect.width;
    const h = 180;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);

    const padding = { top: 20, right: 20, bottom: 30, left: 35 };
    const chartW = w - padding.left - padding.right;
    const chartH = h - padding.top - padding.bottom;

    const data = getData(activeMetric);
    const minVal = Math.max(0, Math.min(...data) - 1);
    const maxVal = Math.min(10, Math.max(...data) + 1);
    const range = maxVal - minVal || 1;

    // Grid lines
    for (let i = 0; i <= 4; i++) {
      const y = padding.top + (chartH * i) / 4;
      const val = maxVal - (range * i) / 4;

      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(w - padding.right, y);
      ctx.strokeStyle = 'rgba(255,255,255,0.04)';
      ctx.lineWidth = 0.5;
      ctx.stroke();

      ctx.font = '9px sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.textAlign = 'right';
      ctx.fillText(val.toFixed(1), padding.left - 5, y + 3);
    }

    // Month labels
    ctx.textAlign = 'center';
    history.forEach((h, i) => {
      const x = padding.left + (chartW * i) / (history.length - 1);
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.font = '9px sans-serif';
      ctx.fillText(h.month, x, h.length - padding.bottom + 45);
    });

    // Draw all metric lines (faded) then active line on top
    metrics.forEach(metric => {
      const mData = getData(metric.key);
      const isActive = metric.key === activeMetric;
      
      ctx.beginPath();
      mData.forEach((val, i) => {
        const x = padding.left + (chartW * i) / (history.length - 1);
        const y = padding.top + chartH - ((val - minVal) / range) * chartH;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.strokeStyle = isActive ? metric.color : `${metric.color}22`;
      ctx.lineWidth = isActive ? 2 : 1;
      ctx.stroke();

      // Fill area under active line
      if (isActive) {
        const lastIdx = mData.length - 1;
        const lastX = padding.left + chartW;
        const firstX = padding.left;
        ctx.lineTo(lastX, padding.top + chartH);
        ctx.lineTo(firstX, padding.top + chartH);
        ctx.closePath();
        
        const gradient = ctx.createLinearGradient(0, padding.top, 0, padding.top + chartH);
        gradient.addColorStop(0, `${metric.color}15`);
        gradient.addColorStop(1, `${metric.color}02`);
        ctx.fillStyle = gradient;
        ctx.fill();
      }

      // Draw dots on active
      if (isActive) {
        mData.forEach((val, i) => {
          const x = padding.left + (chartW * i) / (history.length - 1);
          const y = padding.top + chartH - ((val - minVal) / range) * chartH;
          
          ctx.beginPath();
          ctx.arc(x, y, 3.5, 0, Math.PI * 2);
          ctx.fillStyle = metric.color;
          ctx.fill();
          ctx.strokeStyle = '#0a0a0a';
          ctx.lineWidth = 1.5;
          ctx.stroke();

          // Value label
          ctx.font = 'bold 9px sans-serif';
          ctx.fillStyle = metric.color;
          ctx.textAlign = 'center';
          ctx.fillText(val.toFixed(1), x, y - 8);
        });
      }
    });

    // Month labels at bottom
    history.forEach((h, i) => {
      const x = padding.left + (chartW * i) / (history.length - 1);
      ctx.fillStyle = 'rgba(255,255,255,0.35)';
      ctx.font = '9px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(h.month, x, h - padding.bottom + 80);
    });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border/40 rounded-sm p-5"
      data-testid="development-timeline"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <TrendingUp size={14} className="text-primary" /> Development Timeline
        </h2>
        
        {/* Trend Indicator */}
        <div className="flex items-center gap-2">
          {trend > 0 && (
            <span className="flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-sm">
              <ArrowUp size={12} /> +{trend.toFixed(1)}
            </span>
          )}
          {trend < 0 && (
            <span className="flex items-center gap-1 text-xs text-red-400 bg-red-500/10 px-2 py-1 rounded-sm">
              <ArrowDown size={12} /> {trend.toFixed(1)}
            </span>
          )}
          {trend === 0 && (
            <span className="flex items-center gap-1 text-xs text-zinc-400 bg-zinc-500/10 px-2 py-1 rounded-sm">
              <Minus size={12} /> 0.0
            </span>
          )}
        </div>
      </div>

      {/* Metric Toggles */}
      <div className="flex gap-1 mb-4 flex-wrap">
        {metrics.map(m => (
          <button
            key={m.key}
            onClick={() => setActiveMetric(m.key)}
            className={`text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-all ${
              activeMetric === m.key
                ? 'border-opacity-50 font-medium'
                : 'border-border/20 text-muted-foreground hover:text-foreground'
            }`}
            style={activeMetric === m.key ? { 
              borderColor: m.color + '50',
              backgroundColor: m.color + '10',
              color: m.color
            } : {}}
            data-testid={`timeline-toggle-${m.key}`}
          >
            <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: m.color }} />
            {m.label}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div ref={containerRef} className="w-full">
        <canvas ref={canvasRef} />
      </div>

      {/* Score Summary */}
      <div className="grid grid-cols-4 gap-2 mt-4 pt-3 border-t border-border/20">
        {metrics.map(m => {
          const vals = getData(m.key);
          const current = vals[vals.length - 1];
          const first = vals[0];
          const change = current - first;
          return (
            <div key={m.key} className="text-center">
              <p className="text-[10px] text-muted-foreground uppercase">{m.label}</p>
              <p className="font-mono text-sm font-bold" style={{ color: m.color }}>{current?.toFixed(1)}</p>
              <p className={`text-[10px] ${change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {change >= 0 ? '+' : ''}{change.toFixed(1)}
              </p>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default DevelopmentTimeline;
